import Breadcrumb from "components/Common/Breadcrumb2"
import toast from "components/Toast/Toast"
import moment from "moment"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import BootstrapTable from "react-bootstrap-table-next"
import paginationFactory, {
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import { withTranslation } from "react-i18next"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap"
import {
  addChannel,
  deleteChannel,
  getAllChannels,
  patchChannel,
  restoreChannel,
} from "services/support/calls"

const limit = 20

const initPageOptions = {
  sizePerPage: limit,
  totalSize: 0,
  custom: true,
  showTotal: true,
  hideSizePerPage: true,
  hidePageListOnlyOnePage: true,
  prePageText: "Anterior",
  nextPageText: "Próximo",
  paginationTotalRenderer: () => {
    return (
      <div className="d-inline">
        <span className="react-bootstrap-table-pagination-total">
          Registros Retornados: {total}
        </span>
      </div>
    )
  },
}

const CanalAtendimento = props => {
  document.title = "Cadastro de Canais de Atendimento | Sensetek"
  moment.locale("pt-br")
  const { t } = props

  const nameRef = useRef()
  const modalidadeRef = useRef()
  const novoNameRef = useRef()
  const novaModalidadeRef = useRef()
  const [isLoading, setIsLoading] = useState(true)
  const [canais, setCanais] = useState([])
  const [confirm, setConfirm] = useState({})
  const [showConfirm, setShowConfirm] = useState(false)

  const defaultSorted = [
    {
      dataField: "id",
      order: "asc",
    },
  ]

  const selectRow = {
    mode: "checkbox",
    hideSelectColumn: true,
  }

  const fetchChannels = async () => {
    try {
      const canais = await getAllChannels()
      setCanais(canais)
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    try {
      setIsLoading(true)
      fetchChannels()
    } catch (error) {
      console.log(error)
    }
  }, [])

  const handleSubmit = async e => {
    e.preventDefault()
    setIsLoading(true)
    try {
      if (nameRef.current.value) {
        await addChannel(nameRef.current.value, modalidadeRef.current.value)
        fetchChannels()
        toast("success", "Canal adicionado com sucesso!")
      } else {
        toast("warning", "Não é possível adicionar canal com nome vazio.")
        nameRef.current.focus()
      }
    } catch (error) {
      toast("warning", "Falha ao criar canal.")
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const columns = [
    {
      dataField: "id",
      text: "#",
      sort: false,
      headerStyle: {
        width: "50px",
      },
      formatter: (cellContent, row) => <b>{cellContent}</b>,
    },
    {
      dataField: "name",
      text: "Nome",
      sort: false,
    },
    {
      dataField: "status",
      isDummyField: true,
      text: "Situação",
      headerAlign: "center",
      align: "center",
      headerStyle: (colum, colIndex) => {
        return { width: "10%", textAlign: "center" }
      },
      formatter: (cellContent, row) =>
        row.deletedAt ? (
          <Badge color="warning" className="me-1">
            <b>Inativo</b>
          </Badge>
        ) : (
          <Badge color="primary" className="me-1">
            <b>Ativo</b>
          </Badge>
        ),
    },
    {
      dataField: "shippingModality",
      text: "Modalidade",
      headerAlign: "center",
      align: "center",
      sort: false,
    },
    {
      dataField: "createdAt",
      text: "Criação",
      headerAlign: "center",
      sort: false,
      align: "center",
      formatter: (cellContent, row) => (
        <b>{moment(cellContent, "YYYY-MM-DD").format("DD/MM/YYYY")}</b>
      ),
    },
    {
      dataField: "updatedAt",
      text: "Atualização",
      headerAlign: "center",
      sort: false,
      align: "center",
      formatter: (cellContent, row) => (
        <b>
          {row.updatedAt
            ? moment(cellContent, "YYYY-MM-DD").format("DD/MM/YYYY")
            : "-"}
        </b>
      ),
    },
    {
      dataField: "actions",
      isDummyField: true,
      text: "Ações",
      headerAlign: "center",
      align: "center",
      headerStyle: (colum, colIndex) => {
        return { width: "10%", textAlign: "center" }
      },
      formatter: (cellContent, row) => (
        <ul className="list-unstyled hstack gap-1 mb-0 justify-content-center">
          <li>
            <a
              target="_blank"
              className={`btn btn-sm btn-primary`}
              onClick={() => {
                setConfirm({
                  title: "Confirmação",
                  body: (
                    <div>
                      <Row>
                        <Col>
                          <Label>{t("Novo Nome")}</Label>
                          <Input
                            type="text"
                            className="form-control"
                            defaultValue={row.name}
                            ref={novoNameRef}
                            disabled={isLoading}
                            onChange={e => {
                              novoNameRef.current.value = e.target.value
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Label>{t("Nova Modalidade")}</Label>
                          <Input
                            type="select"
                            className="form-control"
                            defaultValue={row.shippingModality}
                            ref={novaModalidadeRef}
                            disabled={isLoading}
                            onChange={e => {
                              novaModalidadeRef.current.value = e.target.value
                            }}
                          >
                            <option value={""}>Modalidade</option>
                            <option value={"PORTA_A_PORTA"}>
                              PORTA_A_PORTA
                            </option>
                            <option value={"CORPORATIVO"}>CORPORATIVO</option>
                          </Input>
                        </Col>
                      </Row>
                    </div>
                  ),
                  onConfirm: async () => {
                    const novoNome = novoNameRef.current.value || row.name
                    const novaModalidade =
                      novaModalidadeRef.current.value === undefined
                        ? row.shippingModality
                        : novaModalidadeRef.current.value
                    try {
                      setIsLoading(true)
                      await patchChannel(row.id, novoNome, novaModalidade)
                      fetchChannels()
                      toast("success", "Canal alterado com sucesso!")
                    } catch (error) {
                      toast("warning", "Falha ao alterar canal.")
                      console.log(error)
                    } finally {
                      setIsLoading(false)
                    }
                  },
                })
                setShowConfirm(true)
              }}
            >
              <i
                className="mdi mdi-account-edit"
                id={`altToolTip${row.id}`}
              ></i>
            </a>
            <UncontrolledTooltip placement="top" target={`altToolTip${row.id}`}>
              Alterar
            </UncontrolledTooltip>
          </li>
          <li>
            <a
              target="_blank"
              className={`btn btn-sm btn-${
                row.deletedAt ? "secondary" : "danger"
              }`}
              onClick={async () => {
                if (row.deletedAt) {
                  try {
                    setIsLoading(true)
                    await restoreChannel(row.id)
                    fetchChannels()
                    toast("success", "Canal ativado com sucesso!")
                  } catch (error) {
                    toast("warning", "Falha ao ativar canal.")
                    console.log(error)
                  } finally {
                    setIsLoading(false)
                  }
                } else {
                  setConfirm({
                    title: "Confirmação",
                    body: (
                      <div>
                        <p>Esta ação ira desabilitar esta opção de canal.</p>
                        <p>
                          Por favor, confirme se tem certeza que deseja
                          desabilitar este canal de atendimento?
                        </p>
                      </div>
                    ),
                    onConfirm: async () => {
                      setIsLoading(true)
                      try {
                        await deleteChannel(row.id)
                        fetchChannels()
                        toast("success", "Canal desativado com sucesso!")
                      } catch (error) {
                        toast("warning", "Falha ao ativar canal.")
                        console.log(error)
                      } finally {
                        setIsLoading(false)
                      }
                    },
                  })
                  setShowConfirm(true)
                }
              }}
            >
              <i
                className={`mdi mdi-${
                  row.deletedAt ? "account-reactivate" : "delete-forever"
                }`}
                id={`delToolTip${row.id}`}
              ></i>
            </a>
            <UncontrolledTooltip placement="top" target={`delToolTip${row.id}`}>
              {`${row.deletedAt ? "Reativar" : "Desativar"}`}
            </UncontrolledTooltip>
          </li>
        </ul>
      ),
    },
  ]

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={props.t("Configurações")}
            breadcrumbItems={[
              { title: props.t("Canal de Atendimento"), path: "#" },
            ]}
          />
          <Row>
            <Col>
              <Card className="mb-0 border-bottom">
                <CardHeader tag={"h5"} className="bg-transparent border-bottom">
                  {t("Adicionar")}
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col sm={9}>
                        <FormGroup>
                          <br></br>
                          <Label>{t("Nome do Canal")}</Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Canal de Atendimento"
                            ref={nameRef}
                            disabled={isLoading}
                            onChange={e => {
                              nameRef.current.value = e.target.value
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col sm={2}>
                        <FormGroup>
                          <br></br>
                          <Label>{t("Modalidade")}</Label>
                          <Input
                            type="select"
                            className="form-control"
                            placeholder="Modalidade"
                            ref={modalidadeRef}
                            disabled={isLoading}
                            onChange={e => {
                              modalidadeRef.current.value = e.target.value
                            }}
                          >
                            <option value={""}>Modalidade</option>
                            <option value={"PORTA_A_PORTA"}>
                              PORTA_A_PORTA
                            </option>
                            <option value={"CORPORATIVO"}>CORPORATIVO</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col className="d-flex align-items-end">
                        <FormGroup>
                          <Button type="submit">{t("Adicionar")}</Button>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card>
                <CardHeader tag={"h5"} className="bg-transparent border-bottom">
                  Lista de Canais
                </CardHeader>
                <CardBody>
                  {!isLoading &&
                    (canais.length > 0 ? (
                      <PaginationProvider
                        pagination={paginationFactory(initPageOptions)}
                        keyField="id"
                        columns={columns}
                        data={canais}
                      >
                        {({ paginationTableProps }) => (
                          <ToolkitProvider
                            keyField="id"
                            columns={columns}
                            columnToggle
                            data={canais}
                            search
                          >
                            {toolkitProps => (
                              <React.Fragment>
                                <Row>
                                  <Col xl="12">
                                    <BootstrapTable
                                      keyField={"id"}
                                      bordered={true}
                                      striped={true}
                                      condensed
                                      defaultSorted={defaultSorted}
                                      selectRow={selectRow}
                                      classes={
                                        "table align-middle table-nowrap"
                                      }
                                      headerWrapperClasses={"thead-light"}
                                      {...toolkitProps.baseProps}
                                      {...paginationTableProps}
                                    />
                                  </Col>
                                </Row>
                              </React.Fragment>
                            )}
                          </ToolkitProvider>
                        )}
                      </PaginationProvider>
                    ) : (
                      <div style={{ textAlign: "center" }}>
                        <h5>Nenhum dado foi encontrado.</h5>
                      </div>
                    ))}
                  {isLoading && (
                    <div
                      className="accordion-body"
                      style={{ textAlign: "center" }}
                    >
                      <Spinner className="ms-2" color="primary" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Modal
        isOpen={showConfirm}
        toggle={() => {
          setShowConfirm(!showConfirm)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            <p>{confirm?.title}</p>
          </h5>
          <button
            type="button"
            onClick={() => {
              setShowConfirm(false)
              {
                confirm
              }
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">{confirm?.body}</div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => {
              setShowConfirm(false)
            }}
            className="btn btn-secondary"
            data-dismiss="modal"
          >
            {t("Cancelar")}
          </button>
          <button
            type="button"
            onClick={async () => {
              await confirm?.onConfirm()
              setShowConfirm(false)
            }}
            className="btn btn-primary"
            data-dismiss="modal"
          >
            {t("Confirmar")}
          </button>
        </div>
      </Modal>
    </>
  )
}

CanalAtendimento.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(CanalAtendimento)
